import React, { useEffect, useMemo, useState } from "react";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../../utils/Functions";
import moment from "moment";
import { MdCall } from "react-icons/md";

const PatientHistory = ({
    selectedPatient,
    dischargeSummaries = [],
    dischargeSummariesLoading = false,
    dischargeSummariesError = "",
}) => {
    const [showMap, setShowMap] = useState(false);

    // Close map when patient changes (prevents stale modal)
    useEffect(() => {
        setShowMap(false);
    }, [selectedPatient?._id]);

    const headerColor = "#546bb0";

    /**
     * ✅ IMPORTANT SCROLL FIX
     * - The old component used multiple `overflow-hidden` + fixed heights, so the column couldn't scroll reliably.
     * - Now the RIGHT PANEL has ONE scroll container (flex-1 + min-h-0 + overflow-y-auto).
     * - Works across laptop sizes because height is tied to viewport: calc(100vh - 96px).
     *   Adjust 96px if your page has a bigger top header.
     */
    const panelHeightClass = "h-[calc(100vh-96px)] max-h-[calc(100vh-96px)]";

    // Prefer visit address first (most accurate for current booking), fallback to patient profile address
    const visitAddressInfo = selectedPatient?.visitInfo?.addressInfo;
    const profileAddressInfo = selectedPatient?.patientInfo?.personalInfo?.addressInfo;
    const addressInfo = visitAddressInfo || profileAddressInfo || {};

    const lat = addressInfo?.coordinates?.lat;
    const lng = addressInfo?.coordinates?.lng;

    const googleMapUrl =
        lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : "";

    const services = selectedPatient?.visitInfo?.services || [];

    // phone: handle multiple shapes (relationship vs relation + nested otherInfo)
    const relationship = String(selectedPatient?.patient?.relationship || selectedPatient?.patient?.relation || "")
        .trim()
        .toLowerCase();

    const phone =
        relationship === "self"
            ? selectedPatient?.patientInfo?.phone ||
            selectedPatient?.patient?.phone ||
            selectedPatient?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.phone ||
            ""
            : selectedPatient?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.phone ||
            selectedPatient?.patientInfo?.phone ||
            selectedPatient?.patient?.phone ||
            "";

    const specialistRole = String(selectedPatient?.specialistInfo?.role || "").toLowerCase();
    const dutyType = String(selectedPatient?.dutyType || "").toLowerCase();
    const rawIsAssessment = selectedPatient?.isAssessment;

    const isAssessmentPhysioVisit =
        specialistRole === "physiotherapist" &&
        (rawIsAssessment === true || String(rawIsAssessment).toLowerCase() === "true");

    const isNonAssessmentPhysioVisit =
        specialistRole === "physiotherapist" &&
        (rawIsAssessment === false || String(rawIsAssessment).toLowerCase() === "false");

    const isNonAssessmentNurseOneTimeVisit =
        specialistRole === "nurse" &&
        dutyType === "onetime" &&
        (rawIsAssessment === false || String(rawIsAssessment).toLowerCase() === "false");

    const isNurseRegularVisit = specialistRole === "nurse" && dutyType === "regular";

    const orderServiceFees = Array.isArray(selectedPatient?.paymentInfo?.serviceFees)
        ? selectedPatient.paymentInfo.serviceFees
        : [];

    const orderAmountTotal = orderServiceFees.reduce(
        (sum, item) => sum + Number(item?.fee ?? item?.fees ?? 0),
        0
    );

    const nurseFeePerDay = Number(selectedPatient?.paymentInfo?.feePerDay ?? 0);
    const nurseTotalFees = Number(selectedPatient?.paymentInfo?.totalFees ?? 0);
    const nurseTotalDays = Number(selectedPatient?.paymentInfo?.totalDays ?? 0);

    const physioAssessmentFee = Number(selectedPatient?.paymentInfo?.assessment?.fee ?? 0);
    const physioSessions = Array.isArray(selectedPatient?.paymentInfo?.sessions)
        ? selectedPatient.paymentInfo.sessions
        : [];

    const physioExtensionTotal = physioSessions.reduce(
        (sum, session) => sum + Number(session?.extensionFees ?? session?.grandTotal ?? 0),
        0
    );
    const physioAssessmentTotal = physioAssessmentFee + physioExtensionTotal;

    const preExistingConditionsData =
        selectedPatient?.patientInfo?.preExistingConditions?.completed &&
            selectedPatient?.patientInfo?.preExistingConditions?.data?.length > 0
            ? selectedPatient.patientInfo.preExistingConditions.data
            : [];

    const allergiesData =
        selectedPatient?.patientInfo?.allergies?.completed &&
            selectedPatient?.patientInfo?.allergies?.data?.length > 0
            ? selectedPatient.patientInfo.allergies.data
            : [];

    const medicalHistoryData =
        selectedPatient?.patientInfo?.medicalHistory?.completed &&
            selectedPatient?.patientInfo?.medicalHistory?.data?.length > 0
            ? selectedPatient.patientInfo.medicalHistory.data
            : [];

    const familyHistoryData =
        selectedPatient?.patientInfo?.familyHistory?.completed &&
            selectedPatient?.patientInfo?.familyHistory?.data?.length > 0
            ? selectedPatient.patientInfo.familyHistory.data
            : [];

    const fullAddress = useMemo(() => {
        const addr = [
            addressInfo?.address1,
            addressInfo?.address2,
            addressInfo?.district,
            addressInfo?.city,
            addressInfo?.state,
            addressInfo?.pincode,
        ]
            .filter(Boolean)
            .join(", ");
        return addr || "";
    }, [addressInfo]);

    const hasAnyData = Boolean(selectedPatient);

    const hasPatientHistoryData =
        preExistingConditionsData.length > 0 ||
        allergiesData.length > 0 ||
        medicalHistoryData.length > 0 ||
        familyHistoryData.length > 0;

    const Card = ({ title, children, className = "" }) => (
        <div className={`flex flex-col rounded-[10px] overflow-hidden border border-[#D7E4EB] shadow-sm bg-white ${className}`}>
            <div className="px-4 py-2 flex-shrink-0" style={{ backgroundColor: headerColor }}>
                <h3 className="text-[13px] font-medium text-white whitespace-nowrap">{title}</h3>
            </div>
            <div className="px-4 py-3">{children}</div>
        </div>
    );

    return (
        <div className="w-[240px] xl:w-[320px] flex-shrink-0 font-poppins">
            {!hasAnyData ? (
                <div className={`${panelHeightClass}`}>
                    <Card title="Patient Information" className="h-full">
                        <div className="h-full flex items-center justify-center">
                            <div className="text-[12px] text-[#6b6b6b] text-center">No Information to display</div>
                        </div>
                    </Card>
                </div>
            ) : (
                <div className={`sticky top-2 ${panelHeightClass} flex flex-col min-h-0`}>
                    {/* ✅ ONE scroll container for the whole right panel */}
                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1 space-y-4">
                        <Card title="Patient's Conditions / Complaint's">
                            <div className="text-[12px] text-[#464646] leading-relaxed">
                                {services.length > 0 ? (
                                    services.map((service, index) => (
                                        <span key={`${service}-${index}`}>
                                            {service}
                                            {index < services.length - 1 && ", "}
                                        </span>
                                    ))
                                ) : (
                                    <span className="italic text-[#6b6b6b]">No services listed</span>
                                )}
                            </div>
                        </Card>

                        <Card title="Contact Information">
                            <div className="space-y-3">
                                <div className="flex text-[12px] gap-1">
                                    <span className="text-[#2d2d2d] font-semibold min-w-[90px]">Phone Number</span>
                                    <span className="text-[#464646] mx-1">:</span>
                                    <span className="text-[#6b6b6b] flex-1 flex items-center gap-1 break-all">
                                        <MdCall color={headerColor} size={14} />
                                        {formatPhoneNumber(phone) || "-"}
                                    </span>
                                </div>

                                <div className="flex text-[12px] gap-1 items-start">
                                    <span className="text-[#2d2d2d] font-semibold min-w-[90px]">Address</span>
                                    <span className="text-[#464646] mx-1 mt-[1px]">:</span>
                                    <span className="text-[#6b6b6b] flex-1 break-words">
                                        {fullAddress ? (
                                            <button
                                                onClick={() => setShowMap(true)}
                                                className="underline text-[#2d2d2d] hover:text-[#4E7DA6] text-left transition-colors break-words"
                                            >
                                                {fullAddress}
                                            </button>
                                        ) : (
                                            "-"
                                        )}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        <Card title="Discharge Summaries">
                            <div className="space-y-2">
                                {dischargeSummariesLoading ? (
                                    <div className="text-[12px] text-[#6b6b6b] italic">Loading discharge summaries...</div>
                                ) : dischargeSummariesError ? (
                                    <div className="text-[12px] text-red-500">{dischargeSummariesError}</div>
                                ) : Array.isArray(dischargeSummaries) && dischargeSummaries.length > 0 ? (
                                    dischargeSummaries.map((item, index) => (
                                        <div
                                            key={item?._id || `${item?.title || "summary"}-${index}`}
                                            className="border border-gray-100 rounded-lg px-2 py-1.5 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="text-[12px] text-[#2d2d2d] font-semibold break-words">
                                                        {item?.title || `Discharge Summary ${index + 1}`}
                                                    </div>
                                                    <div className="text-[10px] text-[#6b6b6b] mt-0.5">
                                                        {item?.date ? moment(item.date).format("DD MMM YYYY, hh:mm A") : "-"}
                                                    </div>
                                                </div>

                                                {item?.key ? (
                                                    <a
                                                        href={item.key}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="shrink-0 text-[10px] px-2 py-1 rounded bg-blue-50 text-[#3E57E8] hover:bg-blue-100 transition-colors"
                                                    >
                                                        View
                                                    </a>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-[12px] text-[#6b6b6b] italic">No discharge summaries uploaded</div>
                                )}
                            </div>
                        </Card>

                        {(isNonAssessmentPhysioVisit || isNonAssessmentNurseOneTimeVisit) && (
                            <Card title="Order Amount">
                                <div className="space-y-2">
                                    {orderServiceFees.length > 0 ? (
                                        <>
                                            {orderServiceFees.map((item, index) => {
                                                const serviceName = String(item?.service || item?.name || `Service ${index + 1}`).trim();
                                                const amount = Number(item?.fee ?? item?.fees ?? 0);
                                                return (
                                                    <div
                                                        key={`${serviceName}-${index}`}
                                                        className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5 last:border-0 last:pb-0"
                                                    >
                                                        <span className="text-[#2d2d2d] truncate">{serviceName || "-"}</span>
                                                        <span className="text-[#6b6b6b] whitespace-nowrap">
                                                            Rs. {Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-200 text-[12px] font-semibold">
                                                <span className="text-[#2d2d2d]">Total</span>
                                                <span className="text-[#2d2d2d]">Rs. {orderAmountTotal.toFixed(2)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-[12px] text-[#6b6b6b] italic">No service fees available</div>
                                    )}
                                </div>
                            </Card>
                        )}

                        {isAssessmentPhysioVisit && (
                            <Card title="Order Amount">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                                        <span className="text-[#2d2d2d]">Assessment Fee</span>
                                        <span className="text-[#6b6b6b] whitespace-nowrap">
                                            Rs. {Number.isFinite(physioAssessmentFee) ? physioAssessmentFee.toFixed(2) : "0.00"}
                                        </span>
                                    </div>

                                    {physioSessions.map((session, index) => {
                                        const extendedDays = Number(session?.extendedDays ?? 0);
                                        const feesPerSession = Number(session?.feesPerSession ?? 0);
                                        const extensionAmount = Number(session?.extensionFees ?? session?.grandTotal ?? 0);

                                        return (
                                            <div
                                                key={`physio-session-${index}`}
                                                className="flex items-start justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5 last:border-0 last:pb-0"
                                            >
                                                <span className="text-[#2d2d2d]">
                                                    Session Extension {index + 1}
                                                    {Number.isFinite(extendedDays) && extendedDays > 0 ? ` (${extendedDays} days)` : ""}
                                                    {Number.isFinite(feesPerSession) && feesPerSession > 0 ? ` @ Rs. ${feesPerSession.toFixed(2)}` : ""}
                                                </span>
                                                <span className="text-[#6b6b6b] whitespace-nowrap">
                                                    Rs. {Number.isFinite(extensionAmount) ? extensionAmount.toFixed(2) : "0.00"}
                                                </span>
                                            </div>
                                        );
                                    })}

                                    <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-200 text-[12px] font-semibold">
                                        <span className="text-[#2d2d2d]">Total</span>
                                        <span className="text-[#2d2d2d] whitespace-nowrap">
                                            Rs. {Number.isFinite(physioAssessmentTotal) ? physioAssessmentTotal.toFixed(2) : "0.00"}
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {isNurseRegularVisit && (
                            <Card title="Order Amount">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                                        <span className="text-[#2d2d2d]">Fee Per Day</span>
                                        <span className="text-[#6b6b6b] whitespace-nowrap">
                                            Rs. {Number.isFinite(nurseFeePerDay) ? nurseFeePerDay.toFixed(2) : "0.00"}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                                        <span className="text-[#2d2d2d]">Total Days</span>
                                        <span className="text-[#6b6b6b] whitespace-nowrap">
                                            {Number.isFinite(nurseTotalDays) ? nurseTotalDays : 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 text-[12px] font-semibold">
                                        <span className="text-[#2d2d2d]">Total Fees</span>
                                        <span className="text-[#2d2d2d] whitespace-nowrap">
                                            Rs. {Number.isFinite(nurseTotalFees) ? nurseTotalFees.toFixed(2) : "0.00"}
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        )}

                        <Card title="Patient History">
                            {hasPatientHistoryData ? (
                                <div className="space-y-6">
                                    {preExistingConditionsData.length > 0 && (
                                        <div>
                                            <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                                                Pre-Existing Condition
                                            </h5>
                                            {preExistingConditionsData.map((condition, index) => (
                                                <div
                                                    key={`pre-${index}`}
                                                    className="space-y-1.5 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                                                >
                                                    <div className="flex text-[12px] gap-1">
                                                        <span className="text-[#2d2d2d] font-semibold min-w-[70px]">
                                                            Condition
                                                        </span>
                                                        <span className="text-[#464646] mx-1">:</span>
                                                        <span className="text-[#6b6b6b] flex-1 break-words">
                                                            {condition.condition || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="flex text-[12px] gap-1">
                                                        <span className="text-[#2d2d2d] font-semibold min-w-[70px]">
                                                            Since
                                                        </span>
                                                        <span className="text-[#464646] mx-1">:</span>
                                                        <span className="text-[#6b6b6b] flex-1 break-words">
                                                            {condition.diagnosedDate
                                                                ? moment(condition.diagnosedDate).format("MMM YYYY")
                                                                : condition.sinceWhen || "-"}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {allergiesData.length > 0 && (
                                        <div>
                                            <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                                                Allergies
                                            </h5>
                                            {allergiesData.map((a, i) => (
                                                <div
                                                    key={`alg-${i}`}
                                                    className="space-y-1.5 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                                                >
                                                    <div className="flex text-[12px] gap-1">
                                                        <span className="text-[#2d2d2d] font-semibold min-w-[70px]">
                                                            Type
                                                        </span>
                                                        <span className="text-[#464646] mx-1">:</span>
                                                        <span className="text-[#6b6b6b] flex-1 break-words">
                                                            {a.allergen || "-"}
                                                        </span>
                                                    </div>
                                                    {a.allergenReason && (
                                                        <div className="flex text-[12px] gap-1">
                                                            <span className="text-[#2d2d2d] font-semibold min-w-[70px]">
                                                                Reason
                                                            </span>
                                                            <span className="text-[#464646] mx-1">:</span>
                                                            <span className="text-[#6b6b6b] flex-1 break-words">
                                                                {a.allergenReason}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {medicalHistoryData.length > 0 && (
                                        <div>
                                            <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                                                Medical History
                                            </h5>
                                            {medicalHistoryData.map((m, i) => (
                                                <div
                                                    key={`mh-${i}`}
                                                    className="flex text-[12px] gap-1 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                                                >
                                                    <span className="text-[#2d2d2d] font-semibold min-w-[70px]">
                                                        Diagnosis
                                                    </span>
                                                    <span className="text-[#464646] mx-1">:</span>
                                                    <span className="text-[#6b6b6b] flex-1 break-words">
                                                        {m.diagnosis || "-"}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {familyHistoryData.length > 0 && (
                                        <div>
                                            <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                                                Family Medical History
                                            </h5>
                                            {familyHistoryData.map((f, i) => (
                                                <div
                                                    key={`fh-${i}`}
                                                    className="space-y-1.5 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                                                >
                                                    <div className="flex text-[12px] gap-1">
                                                        <span className="text-[#2d2d2d] font-semibold min-w-[80px]">
                                                            Relation
                                                        </span>
                                                        <span className="text-[#464646] mx-1">:</span>
                                                        <span className="text-[#6b6b6b] flex-1 break-words">
                                                            {capitalizeFirstLetter(f.relationToPatient || "-")}
                                                        </span>
                                                    </div>

                                                    {f.medicalCondition && (
                                                        <div className="flex text-[12px] gap-1">
                                                            <span className="text-[#2d2d2d] font-semibold min-w-[80px]">
                                                                Condition
                                                            </span>
                                                            <span className="text-[#464646] mx-1">:</span>
                                                            <span className="text-[#6b6b6b] flex-1 break-words">
                                                                {f.medicalCondition}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {f.diagnosedAge && (
                                                        <div className="flex text-[12px] gap-1">
                                                            <span className="text-[#2d2d2d] font-semibold min-w-[80px]">
                                                                Age
                                                            </span>
                                                            <span className="text-[#464646] mx-1">:</span>
                                                            <span className="text-[#6b6b6b] flex-1 break-words">
                                                                {f.diagnosedAge}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-[12px] text-[#6b6b6b] italic text-center py-2">
                                    No Information to display
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            )}

            {/* Google Map Modal */}
            {showMap && fullAddress && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-poppins">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-hidden">
                        <div className="flex justify-between items-center p-4" style={{ backgroundColor: headerColor }}>
                            <h3 className="text-[16px] font-semibold text-white">Patient Address Location</h3>
                            <button onClick={() => setShowMap(false)} className="text-white text-xl hover:opacity-80">
                                ×
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="text-[13px] text-[#2d2d2d] mb-3">{fullAddress}</div>

                            {lat && lng ? (
                                <iframe
                                    title="Patient Location"
                                    src={`https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
                                    width="100%"
                                    height="400"
                                    className="rounded-lg border border-gray-200"
                                    loading="lazy"
                                    allowFullScreen
                                />
                            ) : (
                                <div className="text-sm text-red-500">Location coordinates not available.</div>
                            )}

                            {googleMapUrl && (
                                <div className="mt-4 text-right">
                                    <a
                                        href={googleMapUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#4E7DA6] text-white text-sm px-4 py-2 rounded-md hover:bg-[#3b6a8a] transition"
                                    >
                                        Open in Google Maps
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientHistory;